import { useState, useCallback, useRef } from 'react';
import { useSessionJwt } from '../hooks/useSessionJwt';
import { useEnvironment } from '../contexts/EnvironmentContext';
import { useTimeRange } from '../contexts/TimeRangeContext';
import { v2ServicePath } from '../config';

// ── normalizers ────────────────────────────────────────────────────────────

function normalizeV2Result(r) {
  return {
    trace_id:        r.trace_id,
    trace_start:     r.start_time ?? null,
    start_time:      r.start_time ?? null,
    session_id:      r.session_id ?? null,
    user_id:         r.user_id ?? null,
    duration_ms:     r.duration_ms ?? null,
    span_count:      r.span_count ?? 0,
    tokens:          (r.total_input_tokens ?? 0) + (r.total_output_tokens ?? 0),
    errors:          r.error_count ?? 0,
    cost:            0,
    input:           r.first_user_input ?? null,
    output:          r.last_assistant_output ?? null,
    models:          Array.isArray(r.model_names) ? r.model_names : [],
    agents:          Array.isArray(r.agent_names) ? r.agent_names : [],
    tools:           Array.isArray(r.tool_names)  ? r.tool_names  : [],
    has_error:       r.has_error ?? false,
    relevance_score: r.relevance_score ?? 0,
    workflow:        r.workflow ?? null,
  };
}

// ── hook ───────────────────────────────────────────────────────────────────

/**
 * POST-based search hook — user-triggered, not auto-fetched on mount.
 *
 * Returns { results, loading, error, search, clear }
 *   search(query, filters) — execute a search; aborts any in-flight request
 *   clear()               — reset results to null (search bar cleared)
 *
 * `results` is null when no search has been submitted, [] when search returned
 * nothing. Callers can distinguish "not searched yet" from "no results".
 */
export function useSearch(selectedAppId) {
  const [results, setResults]   = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const abortRef                = useRef(null);

  const { getJwt }              = useSessionJwt();
  const { selectedEnvironment } = useEnvironment();
  const { selectedRange }       = useTimeRange();

  const search = useCallback(async (query, filters = {}) => {
    const q = (query || '').trim();
    if (!q || !selectedAppId) return;

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const jwt = getJwt();
    if (!jwt) return;

    setLoading(true);
    setError(null);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';

      const url = `${apiUrl}${v2ServicePath(selectedAppId, '/traces/search')}?env=${encodeURIComponent(selectedEnvironment)}`;
      // Structured facets go in a dedicated classification_filters object.
      // Only the current search facet keys are forwarded.
      const FACET_KEYS = ['agent_name', 'tools_called', 'models_used', 'had_error', 'workflow_name', 'outcome'];
      const classificationFilters = {};
      for (const key of FACET_KEYS) {
        const value = filters[key];
        if (value != null && value !== '') classificationFilters[key] = value;
      }
      const body = { query: q, limit: 50, range: selectedRange, classification_filters: classificationFilters };

      const response = await fetch(url, {
        method: 'POST',
        signal: controller.signal,
        headers: {
          Authorization: `Bearer ${jwt}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) throw new Error(`Search failed: ${response.status}`);
      const data = await response.json();

      const raw = data.data ?? [];
      setResults(raw.map(normalizeV2Result));
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Search error:', err);
        setError(err.message);
        setResults([]);
      }
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [selectedAppId, selectedEnvironment, selectedRange, getJwt]);

  const clear = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    setResults(null);
    setError(null);
    setLoading(false);
  }, []);

  return { results, loading, error, search, clear };
}
